import React, { useEffect, useState } from 'react';
import { jsPDF } from 'jspdf';
import styled from 'styled-components';
import Airplay from '@material-ui/icons/Airplay';
import { Button } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import history from '../../../history';
import { getSingleFormReport } from '../../../api/reports';
import { setPdfReport } from '../../../redux/slices/inspectionsSlice';
import useSelectedFormImages from '../../../organisms/ThresholdReports/useSelectedFormImages';
import { Loader } from '../../../atoms';
import ModalInfo from '../../../molecules/ModalInfo';
import { ROUTES } from '../../../constants/routes';
import { RootState } from '../../../redux/root';
import { getSignature } from '../../../api/userAuth';
import { InspectionStatuses } from '../../../constants';
import { overlayInfo, THRESHOLD_FONTSIZE } from '../../../organisms/ThresholdReports/docOvelayTool';
import { buildReportBlob, fetchSourcePdf } from '../../../organisms/ThresholdReports/mergeSourcePdf';
import { findMissingForReport, MissingField } from '../../../organisms/ThresholdReports/fillLayout';

const base64ToBlob = (b64: string, type = 'application/pdf') => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type });
};

const downloadBlob = (blob: Blob, name: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const PDF = ({ initialValues, setInspectionReport, selectedStatusValue, getValues, watch, setPdfObject, setReportPdf }) => {
  const [open, setOpen] = useState<boolean>(false);
  // What kept the threshold report from being created; empty when nothing did.
  const [missingFields, setMissingFields] = useState<MissingField[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const dispatch = useDispatch();
  const [pdf, setPdf] = useState<any>();
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [signature, setSignature] = useState<string>('');
  const userId = useSelector((s: RootState) => s.userAuth.info.id);
  const selectedInspection = useSelector((s: RootState) => s.inspections.selectedInspection);
  const selectedForm = useSelector((s: RootState) => s.thresholdForms.selectedForm);
  // The form's page images arrive from a separate detail fetch. Until they do,
  // overlayInfo would draw the values onto NOTHING and hand back a
  // blank-background report that still uploads and attaches — so block here.
  const { imagesLoading } = useSelectedFormImages();

  const inspectionDate = watch('inspectionDate');
  const inspectionType = watch('inspectionType');
  const information = watch('information');

  useEffect(() => {
    if (userId) {
      const requestSignature = async () => {
        const _signature = await getSignature(userId);
        setSignature(_signature);
      };
      requestSignature();
    }
  }, [userId]);
  // `selectedInspection` is in here because isDisabled() now falls back to it:
  // without it, switching inspections would not re-evaluate the button and it
  // would keep the previous inspection's enabled/disabled state.
  useEffect(() => {
    isDisabled();
  }, [inspectionDate, inspectionType, selectedStatusValue, information, selectedInspection]);

  const onSendReport = () => {
    if (!signature) {
      setOpen(true);
    }

    if (selectedInspection.formMarkup) {
      // This used to demand the inspector's "Completed" tick, which is gone.
      // The form is ready as soon as nothing is missing: required fields
      // filled (the builder marks them) and the outcome recorded — Activity
      // Status fills it on threshold / specialty forms. Anything still blank
      // is listed so the inspector knows exactly what to fill.
      const missing = findMissingForReport(selectedInspection.formMarkup);
      if (missing.length) {
        setMissingFields(missing);
        return;
      }
      createThresholdReport();
    } else {
      createReport(signature);
    }
  };
  const createThresholdReport = async () => {
    // Composite over the form's original PDF when the backend kept one, so the
    // attached report is vector rather than a bitmap of the page.
    const sourceBytes = await fetchSourcePdf(selectedForm?.sourceFile);

    const doc = new jsPDF();
    doc.setFontSize(THRESHOLD_FONTSIZE);
    const { fileBase64 } = selectedForm;
    overlayInfo(doc, fileBase64, selectedInspection.formMarkup, signature, !!sourceBytes);

    const blob = await buildReportBlob(doc, sourceBytes);
    // NOT setPdf(doc): with a source PDF the doc is only the text layer, so
    // Download would hand the user a page of floating values with no form
    // behind them. Save the composited blob instead.
    setPdf(
      sourceBytes
        ? { save: (name: string) => downloadBlob(blob, name || fileName) }
        : doc,
    );
    prepareReportPdf(blob);
  };

  const prepareReportPdf = output => {
    const formattedInspectionDate = format(new Date(initialValues.inspectionDate), 'yyyyMMdd');
    const permitNumber = initialValues.permitNumber || '';
    const inspectionCode = initialValues.activityTypeName ? String(initialValues.activityTypeName).match(/^\d+/)?.[0] || '' : '';
    const _fileName = `${formattedInspectionDate} ${permitNumber} ${inspectionCode} ${initialValues.id}.pdf`.trim();
    setFileName(_fileName);
    const pdfFile = new File([output], _fileName, { type: 'application/pdf' });
    setInspectionReport(pdfFile);
    setReportPdf(pdfFile);
    dispatch(setPdfReport(_fileName));
  };

  const createReport = signUrl => {
    const createInspectionReport = async () => {
      if (!signUrl) {
        return;
      }
      setShowLoader(true);
      const result = await getSingleFormReport({
        date: initialValues.inspectionDate,
        inspector: initialValues.inspector,
        jurisdiction: initialValues.projectJurisdiction,
        inspectionId: initialValues.id,
        statusName: initialValues.statusName,
        signature: signUrl,
        // Sent RAW, not pre-broken.
        //
        // This used to go through breakLines(), which inserts a hard newline every
        // 78 characters -- a leftover from when the report was a PIL raster that
        // did no wrapping of its own. The server renders real text now
        // (form_setup/pdf_render.py), so those breaks were being wrapped a
        // SECOND time: each 78-character chunk filled one line and then ended
        // half way across the next, and every chunk after the first carried
        // breakLines' leading space as an indent. That is the reported
        // "Information creates a new line" mid-sentence.
        //
        // The bulk daily report has always rendered the stored value untouched
        // (reports/views.py), so this also makes the two paths agree instead of
        // printing the same comment two different ways.
        information: getValues('information') === '' ? null : getValues('information'),
      });
      setShowLoader(false);

      // The server now renders the form as a real PDF — the page as its own
      // layer with the values as selectable text. Use it directly.
      //
      // The old path below wrapped the server's PNG in jsPDF, which flattened
      // everything to a bitmap AND distorted it: 220x290mm of artwork on a
      // 210x297mm page, stretched wide and squashed short. It stays only as a
      // fallback for a backend that predates the `pdf` key.
      const serverPdf = result.data[0]?.pdf;
      if (serverPdf) {
        const blob = base64ToBlob(serverPdf);
        // A stand-in for the jsPDF object the callers expect, so "print again"
        // (InspectionSaving) and Download keep working unchanged.
        const saver = { save: (name: string) => downloadBlob(blob, name || fileName) };
        setPdf(saver);
        setPdfObject(saver);
        prepareReportPdf(blob);
        return;
      }

      const _pdf = new jsPDF('p', 'mm', 'a4');
      setPdf(_pdf);
      setPdfObject(_pdf);
      _pdf.addImage(result.data[0].image, 'JPEG', 0, 0, 220, 290);
      const output = _pdf.output('blob');

      prepareReportPdf(output);
    };
    createInspectionReport();
    return false;
  };
  const downloadReport = () => {
    pdf.save(fileName);
  };

  const isDisabled = () => {
    if (initialValues?.isCompleted || selectedStatusValue?.name === InspectionStatuses.SCHEDULED) {
      setButtonDisabled(true);
      return;
    }
    const { inspectionDate } = initialValues;
    if (!inspectionDate) {
      setButtonDisabled(true);
      return;
    }

    // Whether the inspection HAS a type, not whether the dropdown has finished
    // hydrating.
    //
    // This used to read the form field alone. That field is empty until
    // InspectionType.loadType() resolves the numeric id into an option object,
    // and it stays empty whenever that lookup misses — so switching between
    // inspections left Create Report greyed out on a fully valid inspection,
    // with only a page refresh to clear it. Selecting a status re-ran this and
    // disabled it again, because the field was still empty.
    //
    // `selectedInspection.inspectionType` is the underlying value and is set
    // whether or not the dropdown has caught up. The numeric check stays for
    // the form field: a raw id there still means it has not resolved yet, but
    // it is no longer the only thing that can answer the question.
    const inspectionTypeField = getValues('inspectionType');
    const hasResolvedField = !!inspectionTypeField && isNaN(inspectionTypeField);
    if (!hasResolvedField && !selectedInspection?.inspectionType) {
      setButtonDisabled(true);
      return;
    }
    setButtonDisabled(false);
  };

  const onClose = () => {
    setOpen(false);
    history.push(ROUTES.ACCOUNT);
  };
  return (
    <Wrapper>
      {showLoader && (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      )}
      <IconWrapper onClick={downloadReport}>{fileName && !showLoader && <Airplay />}</IconWrapper>
      <Button
        style={{
          margin: 0,
          borderRadius: '6px',
        }}
        disabled={buttonDisabled || imagesLoading}
        variant="contained"
        component="label"
        color="primary"
        onClick={onSendReport}
      >
        Create Report
      </Button>

      {open && <ModalInfo onClose={onClose} title="Signature is Required" errorMsg="Signature is missing - please update" />}
      {missingFields.length > 0 && (
        <ModalInfo
          isError
          // Stays up until dismissed — it is a list to read, not a toast.
          timeToClose={0}
          onClose={() => setMissingFields([])}
          title="Complete the inspection form before creating the report"
          message={
            <MissingList>
              {missingFields.map(m => (
                <li key={`${m.page}:${m.key}`}>
                  <strong>{m.label}</strong> — page {m.pageNumber}
                </li>
              ))}
            </MissingList>
          }
        />
      )}
    </Wrapper>
  );
};

export default PDF;

const Wrapper = styled.div`
  visibility: visible;
  font-size: 2mm;
  display: flex;
  align-items: center;
  width: 185px;
  gap: 7px;
`;

const IconWrapper = styled.div`
  width: 30px;
  height: 24px;
`;

const LoaderWrapper = styled.div`
  width: 0px;
  height: 24px;
  zoom: 0.5;
  position: relative;
  top: -15px;
`;

const MissingList = styled.ul`
  margin: 0;
  padding-left: 18px;
  text-align: left;
  max-height: 40vh;
  overflow-y: auto;

  li + li {
    margin-top: 4px;
  }

  strong {
    font-weight: 600;
    color: var(--mainText);
  }
`;
